"use strict";
/**
 * Shared contract for the role-based Home and Overview pages (NEW-2713).
 *
 * The backend returns raw weekly and daily counts per platform; every
 * derived metric (CTR, CR, CPA, CPM, CPC) is computed from those counts with
 * `computeOverviewMetric`, on both sides, so a tile, a chart point and the
 * by-platform table can never disagree about how a ratio is formed.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OVERVIEW_FINDING_DIRECTIONS = exports.OVERVIEW_INSIGHT_KINDS = exports.OVERVIEW_DEFAULT_CHART_PREFERENCES = exports.OVERVIEW_SPEND_SCOPE_DESCRIPTIONS = exports.OVERVIEW_SPEND_SCOPE_LABELS = exports.OVERVIEW_SPEND_SCOPES = exports.OVERVIEW_MAX_SELECTED_METRICS = exports.OVERVIEW_EVENT_METRICS = exports.OVERVIEW_LOWER_IS_BETTER = exports.OVERVIEW_METRIC_DESCRIPTIONS = exports.OVERVIEW_METRIC_LABELS = exports.OVERVIEW_CHART_METRICS = exports.OVERVIEW_METRICS = exports.OVERVIEW_PLATFORMS = exports.OVERVIEW_VIEWER_ROLES = exports.OVERVIEW_CLIENT_VIEWS = exports.OVERVIEW_DEFAULT_VIEW = exports.OVERVIEW_VIEW_LABELS = exports.OVERVIEW_VIEWS = void 0;
exports.isOverviewView = isOverviewView;
exports.isOverviewSpendScope = isOverviewSpendScope;
exports.sumOverviewCounts = sumOverviewCounts;
exports.computeOverviewMetric = computeOverviewMetric;
exports.overviewWeekOverWeekChange = overviewWeekOverWeekChange;
exports.isOverviewMetric = isOverviewMetric;
exports.isOverviewChartMetric = isOverviewChartMetric;
exports.normalizeOverviewChartPreferences = normalizeOverviewChartPreferences;
/**
 * The Home and Overview views: Explore & exploit (new ideas beside more of
 * what wins), Analytics (the performance-first pages) or Creative (the
 * creative briefing). Staff pick from all three and star a saved default;
 * everyone else picks from the views in OVERVIEW_CLIENT_VIEWS, for the
 * session. A pick without a star lasts the session.
 */
exports.OVERVIEW_VIEWS = ["explore", "analytics", "creative"];
exports.OVERVIEW_VIEW_LABELS = {
    explore: "Explore & exploit",
    analytics: "Analytics",
    creative: "Creative",
};
exports.OVERVIEW_DEFAULT_VIEW = "explore";
/** Analytics is a staff view; clients choose between the other two. */
exports.OVERVIEW_CLIENT_VIEWS = [
    "explore",
    "creative",
];
function isOverviewView(value) {
    return exports.OVERVIEW_VIEWS.includes(value);
}
exports.OVERVIEW_VIEWER_ROLES = ["CIL", "FDC"];
exports.OVERVIEW_PLATFORMS = [
    "META",
    "TIKTOK",
    "GOOGLE",
    "SNAPCHAT",
];
exports.OVERVIEW_METRICS = [
    "spend",
    "ctr",
    "cr",
    "cpa",
    "cpm",
    "cpc",
];
/**
 * The metrics a viewer can put on the chart cards. CPM and CPC stay in
 * OVERVIEW_METRICS for the tiles and findings but are not chart options.
 */
exports.OVERVIEW_CHART_METRICS = [
    "spend",
    "ctr",
    "cr",
    "cpa",
];
exports.OVERVIEW_METRIC_LABELS = {
    spend: "Spend",
    ctr: "CTR",
    cr: "CR",
    cpa: "CPA",
    cpm: "CPM",
    cpc: "CPC",
};
exports.OVERVIEW_METRIC_DESCRIPTIONS = {
    spend: "Total spend",
    ctr: "Link clicks ÷ impressions",
    cr: "Main-event conversions ÷ clicks",
    cpa: "Spend ÷ main-event conversions",
    cpm: "Cost per 1,000 impressions",
    cpc: "Cost per link click",
};
/** A fall in these is good news, so their week-over-week change reads green. */
exports.OVERVIEW_LOWER_IS_BETTER = new Set([
    "cpa",
    "cpm",
    "cpc",
]);
/** Metrics whose denominator or numerator is the main conversion event. */
exports.OVERVIEW_EVENT_METRICS = new Set([
    "cr",
    "cpa",
]);
exports.OVERVIEW_MAX_SELECTED_METRICS = 3;
/**
 * Which spend the Spend tile, chart and table show. NF spend is what ads
 * NewForm made spent, as the market-share data matches them; total is
 * everything the connected ad accounts spent. Other metrics are always
 * account totals: the market-share data only has spend.
 */
exports.OVERVIEW_SPEND_SCOPES = ["nf", "total", "both"];
exports.OVERVIEW_SPEND_SCOPE_LABELS = {
    nf: "NF spend",
    total: "Total spend",
    both: "NF and total",
};
exports.OVERVIEW_SPEND_SCOPE_DESCRIPTIONS = {
    nf: "Spend on ads NewForm made",
    total: "Everything the ad accounts spent",
    both: "NF spend, with the account total behind it",
};
function isOverviewSpendScope(value) {
    return exports.OVERVIEW_SPEND_SCOPES.includes(value);
}
exports.OVERVIEW_DEFAULT_CHART_PREFERENCES = {
    metrics: ["spend", "ctr", "cr"],
    defaultMetric: "spend",
    spendScope: "nf",
};
exports.OVERVIEW_INSIGHT_KINDS = [
    "weekly_summary",
    "winner_called",
    "tests_finished",
];
exports.OVERVIEW_FINDING_DIRECTIONS = [
    "improving",
    "worsening",
    "neutral",
];
function sumOverviewCounts(parts) {
    let spend = 0;
    let impressions = 0;
    let clicks = 0;
    let conversions = null;
    for (const part of parts) {
        spend += part.spend;
        impressions += part.impressions;
        clicks += part.clicks;
        if (part.conversions !== null) {
            conversions = (conversions !== null && conversions !== void 0 ? conversions : 0) + part.conversions;
        }
    }
    return { spend, impressions, clicks, conversions };
}
/**
 * One metric from raw counts. Event metrics use only the platforms that have
 * a main event, so a platform without one never dilutes CR or inflates CPA.
 * Returns null when the metric is undefined for the inputs (no denominator).
 */
function computeOverviewMetric(parts, metric) {
    var _a;
    const all = sumOverviewCounts(parts);
    const withEvent = sumOverviewCounts(parts.filter((part) => part.conversions !== null));
    const conversions = (_a = withEvent.conversions) !== null && _a !== void 0 ? _a : 0;
    switch (metric) {
        case "spend":
            return all.spend;
        case "ctr":
            return all.impressions > 0 ? all.clicks / all.impressions : null;
        case "cr":
            return withEvent.clicks > 0 && withEvent.conversions !== null
                ? conversions / withEvent.clicks
                : null;
        case "cpa":
            return conversions > 0 ? withEvent.spend / conversions : null;
        case "cpm":
            return all.impressions > 0 ? (all.spend / all.impressions) * 1000 : null;
        case "cpc":
            return all.clicks > 0 ? all.spend / all.clicks : null;
    }
}
/** Fractional change, e.g. -0.038 for −3.8%; null when last week is 0 or null. */
function overviewWeekOverWeekChange(thisWeek, lastWeek) {
    if (thisWeek === null || lastWeek === null || lastWeek === 0)
        return null;
    return (thisWeek - lastWeek) / lastWeek;
}
function isOverviewMetric(value) {
    return exports.OVERVIEW_METRICS.includes(value);
}
function isOverviewChartMetric(value) {
    return exports.OVERVIEW_CHART_METRICS.includes(value);
}
/**
 * Coerces a stored or submitted preference into a valid one: chart metrics
 * only (a saved CPM or CPC drops out), de-duplicated, 1–3 of them, and a default that is among them.
 */
function normalizeOverviewChartPreferences(input) {
    const spendScope = input.spendScope && isOverviewSpendScope(input.spendScope)
        ? input.spendScope
        : exports.OVERVIEW_DEFAULT_CHART_PREFERENCES.spendScope;
    const metrics = [
        ...new Set(input.metrics.filter(isOverviewChartMetric)),
    ].slice(0, exports.OVERVIEW_MAX_SELECTED_METRICS);
    if (metrics.length === 0)
        return Object.assign(Object.assign({}, exports.OVERVIEW_DEFAULT_CHART_PREFERENCES), { spendScope });
    const defaultMetric = isOverviewChartMetric(input.defaultMetric) &&
        metrics.includes(input.defaultMetric)
        ? input.defaultMetric
        : metrics[0];
    return { metrics, defaultMetric, spendScope };
}
